import { YDatabase, type DatabaseDiff } from '@databases/collab/y-database';
// eslint-disable-next-line workspaces/require-dependency
import {
  buildDatabaseFromCollabAndContent,
  getOrCreateCollabManager
} from '@databases/collab-api/collab/actions';
import { perf } from '@databases/logs/performance';
import { UserType } from '@databases/model/value';
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import {
  type BasicDatabase,
  type BrokenDatabase,
  type Database,
  type ShallowDatabase
} from '@databases/types/database';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { DATABASE_CONTENT_TYPE } from '../../constants';
import { getDatabaseContentByContentId } from '../../database/service';
import { serializeError } from '../../utils';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { loadAsync } from '../redux';

import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdRequired,
  selectDatabaseByContentIdStatus
} from './selectors';
import databasesSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

export const fakeDatabaseContent = {
  status: 'current',
  _links: {},
  version: {
    number: 1,
    friendlyWhen: '',
    message: '',
    when: '',
    by: {
      type: 'anonymous',
      displayName: '',
      profilePicture: {
        path: ''
      }
    }
  },
  history: {
    createdDate: '',
    createdBy: {
      type: 'anonymous',
      displayName: '',
      profilePicture: {
        path: ''
      }
    },
    lastUpdated: {
      number: 1,
      message: '',
      friendlyWhen: '',
      when: '',
      by: {
        type: 'anonymous',
        displayName: '',
        profilePicture: {
          path: ''
        }
      }
    }
  }
} as const;

/**
 * Load the specified database into the store. If the database is already loaded, no request is made.
 */
export const loadDatabaseByContentId =
  (
    contentId: string,
    {
      forceReload = false,
      throttle = false,
      isInitialLoad = false
    }: {
      forceReload?: boolean;
      throttle?: boolean;
      isInitialLoad?: boolean;
    } = {}
  ): Dispatcher<Promise<Database | BrokenDatabase>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectDatabaseByContentIdStatus(getState(), { contentId }),
      () => {
        dispatch(databasesSlice.actions.loadDatabaseByContentIdPending({ contentId }));
      },
      async () => {
        const existingDatabase = selectDatabaseByContentIdOptional(getState(), {
          contentId
        });

        if (forceReload || !existingDatabase) {
          const databaseContent = await getDatabaseContentByContentId(contentId);
          if (!databaseContent || databaseContent.type !== DATABASE_CONTENT_TYPE) {
            throw new Error(`no content for database with content id ${contentId}`);
          }

          const spaceId = databaseContent.space?.id;
          const referenceId = databaseContent.referenceId;

          if (!spaceId) {
            throw new Error(`no space id for database with content id ${contentId}`);
          }

          if (!referenceId) {
            throw new Error(`no reference id for database with content id ${contentId}`);
          }

          const manager = await dispatch(
            getOrCreateCollabManager({
              contentId,
              spaceId,
              referenceId,
              isInitialLoad
            })
          );

          const db = await new Promise<YDatabase>((resolve) => {
            manager.onLoaded((db) => {
              resolve(db);
            });

            // kick off connection attempt
            manager.connect();
          });

          return buildDatabaseFromCollabAndContent(
            databaseContent,
            db,
            manager.isReadOnly
          );
        }

        return existingDatabase;
      },
      (database) => {
        dispatch(addDatabaseToList(database));
        dispatch(databasesSlice.actions.loadDatabaseByContentIdFinished({ contentId }));
      },
      (error) => {
        dispatch(
          databasesSlice.actions.loadDatabaseByContentIdError({
            contentId,
            error: serializeError(error)
          })
        );
      },
      { forceReload, throttle }
    );

    return selectDatabaseByContentIdRequired(getState(), { contentId });
  };

/**
 * Load the specified database into the store. If the database is already loaded, no request is made.
 */
export const loadDatabaseByReferenceId =
  (
    {
      contentId,
      referenceId,
      spaceId,
      spaceKey,
      title,
      spaceName,
      isInitialLoad = false,
      isPreview = false
    }: {
      contentId: string;
      referenceId: string;
      spaceId: number;
      spaceKey: string;
      title: string;
      spaceName: string;
      isInitialLoad?: boolean;
      isPreview?: boolean;
    },
    {
      forceReload = false,
      throttle = false
    }: {
      forceReload?: boolean;
      throttle?: boolean;
    } = {}
  ): Dispatcher<Promise<Database | BrokenDatabase>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectDatabaseByContentIdStatus(getState(), { contentId }),
      () => {
        dispatch(databasesSlice.actions.loadDatabaseByContentIdPending({ contentId }));
      },
      async () => {
        const existingDatabase = selectDatabaseByContentIdOptional(getState(), {
          contentId
        });

        if (forceReload || !existingDatabase) {
          const manager = await dispatch(
            getOrCreateCollabManager({
              contentId,
              spaceId,
              referenceId,
              isInitialLoad
            })
          );

          let db: YDatabase;
          if (isPreview) {
            db = new YDatabase(undefined, {
              getCurrentUser: () => {
                return {
                  id: 'preview-user',
                  accountId: 'preview-user',
                  nickname: 'Preview User',
                  picture: 'preview-user@example.com',
                  email: 'preview-user@example.com',
                  locale: 'en-US',
                  type: UserType.Referenced
                };
              }
            });
          } else {
            db = await new Promise<YDatabase>((resolve) => {
              manager.onLoaded((db) => {
                resolve(db);

                perf.timeLog('app-initialization', 'database-load-end');
              });

              manager.connect();
            });
          }

          return buildDatabaseFromCollabAndContent(
            {
              id: contentId,
              referenceId,
              space: { id: spaceId, key: spaceKey, name: spaceName },
              type: DATABASE_CONTENT_TYPE,
              title,
              ...fakeDatabaseContent
            },
            db,
            manager.isReadOnly
          );
        }

        return existingDatabase;
      },
      (database) => {
        dispatch(addDatabaseToList(database));
        dispatch(databasesSlice.actions.loadDatabaseByContentIdFinished({ contentId }));
      },
      (error) => {
        dispatch(
          databasesSlice.actions.loadDatabaseByContentIdError({
            contentId,
            error: serializeError(error)
          })
        );
      },
      { forceReload, throttle }
    );

    return selectDatabaseByContentIdRequired(getState(), { contentId });
  };

/**
 * Add a new database to the list of databases. If a database with the same content ID exists already, it is replaced (unless the added
 * database is shallow and a non-shallow database is already in the store).
 */
export const addDatabaseToList = (
  database: Database | ShallowDatabase | BasicDatabase | BrokenDatabase
): AnyAction => addDatabasesToList([database]);

export const markDatabaseAsLoaded = (contentId: string): AnyAction =>
  databasesSlice.actions.loadDatabaseByContentIdFinished({ contentId });

export const addDatabasesToList = (
  databases: Array<Database | ShallowDatabase | BasicDatabase | BrokenDatabase>
): AnyAction =>
  databasesSlice.actions.addDatabasesToList({
    databases
  });

export const applyDatabaseDiff = (diffInfo: {
  contentId: string;
  diff: DatabaseDiff;
  collabDatabase: YDatabase;
  isReadOnly: boolean;
}): AnyAction => databasesSlice.actions.applyDatabaseDiff(diffInfo);
